import { Link } from "react-router-dom";
import type { ContentItem } from "@/types/content";

interface ContentCardProps {
  item: ContentItem;
  variant?: "grid" | "compact";
}

export default function ContentCard({ item, variant = "grid" }: ContentCardProps) {
  const typeLabel = item.type === "movie" ? "电影" : "电视剧";
  const detailPath = `/detail/${item.id}`;

  if (variant === "compact") {
    return (
      <Link
        to={detailPath}
        className="group flex gap-3 rounded-lg bg-background-50 p-2.5 transition-colors hover:bg-background-100"
      >
        <div className="h-24 w-16 flex-shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={item.poster}
            alt={`${item.title} 海报`}
            title={`${item.title} ${typeLabel} 剧情介绍`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex min-w-0 flex-col justify-center">
          <h3 className="truncate font-heading text-sm font-semibold text-foreground-950 group-hover:text-primary-700">
            {item.title}
          </h3>
          <p className="mt-1 text-xs text-secondary-500">
            {item.year} · {item.genres.slice(0, 2).join(" / ")}
          </p>
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
            {item.summary}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={detailPath}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <div className="relative h-64 w-full overflow-hidden bg-background-200">
        <img
          src={item.poster}
          alt={`${item.title} 海报`}
          title={`${item.title} ${typeLabel} 剧情介绍`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute left-2.5 top-2.5 rounded-md bg-primary-950/80 px-2 py-0.5 text-[11px] font-medium text-background-50">
          {typeLabel}
        </span>
        <span className="absolute bottom-2.5 right-2.5 flex items-center gap-1 rounded-md bg-accent-500 px-2 py-0.5 text-[11px] font-semibold text-primary-950">
          <i className="ri-fire-line" />
          {(item.heat / 1000).toFixed(1)}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-heading text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-700">
          {item.title}
        </h3>
        <p className="mt-1 text-xs text-secondary-500">
          {item.year} · {item.region} · {item.duration}
        </p>
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {item.genres.map((genre) => (
            <span
              key={genre}
              className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-800"
            >
              {genre}
            </span>
          ))}
        </div>
        <p className="mt-2.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">
          {item.summary}
        </p>
        <p className="mt-3 text-xs text-foreground-500">
          <span className="font-medium text-foreground-700">主演：</span>
          {item.cast.slice(0, 3).join(" / ")}
        </p>
      </div>
    </Link>
  );
}